import {
  CalendarToday,
  Email,
  LocationOn,
  Phone,
  Wc,
} from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useParams } from "react-router-dom";
import Iconify from "../../components/iconify/Iconify";
import { useNotify } from "../../utils/snackbarUtils";
import { useDispatch, useSelector } from "react-redux";
import { getUserByUid } from "../../reduxStore/slices/userSlice";

// Helper for Tab Panels
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

export default function UserView() {
  const { uid } = useParams();
  const { showSuccess, handleApiError, showError } = useNotify();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const storeId = user?.storeId;
  const [tab, setTab] = useState(0);
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  const getUserDetails = async () => {
    setLoading(true);
    try {
      const userData = await dispatch(getUserByUid({ storeId, uid })).unwrap();
      if (userData) {
        setUserDetails(userData);
      } else {
        showError("User not found");
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (uid) {
      getUserDetails();
    }
  }, [uid]);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const customer = userDetails ? {
    name: `${userDetails.first_name || ""} ${userDetails.last_name || ""}`.trim(),
    mobile: userDetails.mobile_no || "-",
    email: userDetails.email || "-",
    city: userDetails.city || "-",
    address: userDetails.address || "-",
    dob: userDetails.dob || "-",
    gender: userDetails.gender || "-",
    createdAt: userDetails.created_at || "-",
    role: userDetails.roles && userDetails.roles.length ? userDetails.roles.map(r => r.role_name).join(", ") : "-",
  } : {
    name: "Loading...",
    mobile: "",
    email: "",
    city: userDetails?.city || "-",
    address: userDetails?.address || "-",
    dob: userDetails?.dob || "-",
    gender: userDetails?.gender || "-",
    createdAt: userDetails?.created_at || "-",
    role: "-",
  };

  return (
    <>
      <Helmet>
        <title> View User | TrueMenu</title>
      </Helmet>
      <Box
        sx={{
          margin: "25px 60px",
        }}
      >
        <Stack mb={3}>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Typography variant="h5" gutterBottom>
              <Link style={{ textDecoration: "none" }} to="/app/users">
                <IconButton>
                  <Iconify width={24} icon="eva:arrow-back-outline" />
                </IconButton>
              </Link>
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box>
                <Avatar sx={{ bgcolor: "primary.main" }}>
                  {customer.name[0]}
                </Avatar>
              </Box>
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Typography variant="h6">{customer.name}</Typography>
                  {/* <Tooltip title="Edit">
                    <IconButton>
                      <Iconify icon={"eva:edit-fill"} />
                    </IconButton>
                  </Tooltip> */}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {customer.mobile} • {customer.email}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Stack>
        <Box>
          {/* Tabs */}
          <Tabs value={tab} onChange={handleTabChange} aria-label="user tabs">
            <Tab label="User Info" />
          </Tabs>

          {/* Customer Info Tab */}
          <TabPanel value={tab} index={0}>
            <Card sx={{ borderRadius: 3, boxShadow: 3, p: 2 }}>
              <CardContent>
                <Grid
                  container
                  spacing={2}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      Name
                    </Typography>
                    <Typography variant="body1">{customer.name}</Typography>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      Mobile
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Phone fontSize="small" color="primary" />
                      <Typography variant="body1">{customer.mobile}</Typography>
                    </Box>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      Email
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Email fontSize="small" color="primary" />
                      <Typography variant="body1">{customer.email}</Typography>
                    </Box>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      Role
                    </Typography>
                    <Typography variant="body1">{customer.role}</Typography>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      Address
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocationOn fontSize="small" color="primary" />
                      <Typography variant="body1">
                        {customer.address}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      Date of Birth
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CalendarToday fontSize="small" color="primary" />
                      <Typography variant="body1">{customer.dob}</Typography>
                    </Box>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      Gender
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Wc fontSize="small" color="primary" />
                      <Typography variant="body1">{customer.gender}</Typography>
                    </Box>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      City
                    </Typography>
                    <Typography variant="body1">{customer.city}</Typography>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      Created At
                    </Typography>
                    <Typography variant="body1">
                      {customer.createdAt}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </TabPanel>
        </Box>
      </Box>
    </>
  );
}
